import { getReport } from '@app/api/routes/reports'
import { exportFile } from '@app/apiExternalStorage/routes/export'

import { REPORT_STATUSES } from './types'

import type { Report, RetrieveReport } from './types'

type PullReportExecutorFn = (resolve: (file: string) => void, reject: (reason?: unknown) => void) => void
type RetrieveReportFn<RetrieveReportParamsType> = (
  params: RetrieveReportParamsType,
  signal?: AbortSignal
) => Promise<RetrieveReport>
type AwaitReportCompletionFn<RetrieveReportParamsType> = (
  params: RetrieveReportParamsType,
  signal?: AbortSignal
) => Promise<Blob>

const DEFAULT_PULLING_TIME_MS = 1000
const DEFAULT_REPORT_FAILED_MESSAGE = 'Не удалось сформировать отчет. Попробуйте повторить позже.'

export class ReportFailedError extends Error {
  constructor (message = DEFAULT_REPORT_FAILED_MESSAGE) {
    super(message)
    this.name = 'ReportFailedError'
  }
}

export const isReportFailedError = (error: unknown): error is ReportFailedError => error instanceof ReportFailedError

const getNormalizedStatus = (status: Report['status'] | RetrieveReport['status']): string | null => {
  return status?.toUpperCase() ?? null
}

const getReportFailedMessage = ({ message, detail }: Report): string => {
  return message ?? detail ?? DEFAULT_REPORT_FAILED_MESSAGE
}

/**
 * Функция создает executor для Promise, который выполняет long-pulling данных по getReport,
 * когда возвращается статус 'DONE' возвращает uri на ресурс
 *
 * @param id - идентификатор для репорта по ресурсу
 * @param pullingTimeMs - промежуток между вызовами pulling функции в миллисекундах
 */
const createPullReportExecutorFn = (id: string | number, pullingTimeMs: number): PullReportExecutorFn => {
  let intervalId: ReturnType<typeof setTimeout>

  return (resolve, reject) => {
    const pullReport = async (): Promise<void> => {
      try {
        const report = await getReport(id)
        const { status, file } = report
        const normalizedStatus = getNormalizedStatus(status)

        if (normalizedStatus === REPORT_STATUSES.failed) {
          clearInterval(intervalId)
          reject(new ReportFailedError(getReportFailedMessage(report)))
          return
        }

        if (normalizedStatus !== REPORT_STATUSES.done) return

        if (!file) return

        clearInterval(intervalId)
        resolve(file)
      } catch (error) {
        clearInterval(intervalId)
        reject(error)
      }
    }

    intervalId = setInterval(pullReport, pullingTimeMs)
  }
}

/**
 * Функция создает асинхронную функцию, которая получает uri на ресурс и возвращает Blob этого ресурса для скачивания
 *
 * @param retrieveReportFn - функция получающая данные по репорту ресурса
 * @param pullingTimeMs - промежуток между вызовами pulling функции в миллисекундах
 */
export const createAwaitReportCompletionFn = <RetrieveReportParamsType>(
  retrieveReportFn: RetrieveReportFn<RetrieveReportParamsType>,
  pullingTimeMs = DEFAULT_PULLING_TIME_MS
): AwaitReportCompletionFn<RetrieveReportParamsType> => {
  return async (params, signal) => {
    const { id } = await retrieveReportFn(params, signal)

    const pullReportExecutor = createPullReportExecutorFn(id, pullingTimeMs)

    const uri: string = await new Promise<string>(pullReportExecutor)

    return await exportFile(uri)
  }
}
